import * as JSZip from "jszip";
import mammoth from "mammoth";
import { useParseWordDocumentStyles } from "./useParseWordDocumentStyles";
import {
  useStripWordDocumentImagesFromHtml,
  useWordDocumentHtmlConversionOptions,
} from "./useWordDocumentHtmlConversionOptions";
import {
  type ImportedRevisionHeaders,
  useExtractDocxPageHeaders,
} from "./useExtractDocxPageHeaders";
import { useExtractWritingCompanionChecklist } from "./useExtractWritingCompanionChecklist";
import type { ImportedChecklist } from "~/types/ImportedChecklist";

const GOOGLE_DOC_HTML_DEBUG_STORAGE_KEY = "debugGoogleDocImport";
const DEBUG_SNIPPET_LENGTH = 200;

const getSafeArrayBuffer = (data: Uint8Array | ArrayBuffer) => {
  if (data instanceof ArrayBuffer) {
    return data;
  }

  return data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength);
};

const isGoogleDocHtmlDebugEnabled = () => {
  if (
    typeof window === "undefined" ||
    typeof window.localStorage?.getItem !== "function"
  ) {
    return false;
  }

  return window.localStorage.getItem(GOOGLE_DOC_HTML_DEBUG_STORAGE_KEY) === "true";
};

const truncateSnippet = (value?: string | null) => {
  if (!value) return undefined;

  const normalizedValue = value.replace(/\s+/g, " ").trim();
  if (normalizedValue.length <= DEBUG_SNIPPET_LENGTH) {
    return normalizedValue;
  }

  return `${normalizedValue.slice(0, DEBUG_SNIPPET_LENGTH)}...`;
};

const getPatternSnippet = (source: string, pattern: RegExp) => {
  const match = source.match(pattern);
  return truncateSnippet(match?.[0]);
};

const logGoogleDocConversionDebug = ({
  documentXml,
  stylesXml,
  mammothHtml,
  parsedHtml,
}: {
  documentXml: string;
  stylesXml?: string;
  mammothHtml: string;
  parsedHtml: string;
}) => {
  if (!isGoogleDocHtmlDebugEnabled()) {
    return;
  }

  console.groupCollapsed("[GoogleDocImport] DOCX conversion diagnostics");
  console.log("stylesXmlPresent", Boolean(stylesXml));
  console.log("documentXmlCounts", {
    underlineNodes: (documentXml.match(/<w:u\b/g) ?? []).length,
    tabNodes: (documentXml.match(/<w:tab\b/g) ?? []).length,
    runStyleNodes: (documentXml.match(/<w:rStyle\b/g) ?? []).length,
  });
  console.log("xmlSnippets", {
    underlineRun: getPatternSnippet(documentXml, /<w:r[\s\S]*?<w:u\b[\s\S]*?<\/w:r>/),
    tabRun: getPatternSnippet(documentXml, /<w:r[\s\S]*?<w:tab\b[\s\S]*?<\/w:r>/),
    styledRun: getPatternSnippet(
      documentXml,
      /<w:r[\s\S]*?<w:rStyle\b[\s\S]*?<\/w:r>/
    ),
  });
  console.log("htmlSnippets", {
    mammothUnderline: getPatternSnippet(
      mammothHtml,
      /<(u|span)\b[^>]*?(text-decoration[^"]*underline|text-decoration[^']*underline)[\s\S]*?>(.*?)<\/(u|span)>/i
    ),
    parsedUnderline: getPatternSnippet(
      parsedHtml,
      /<(u|span)\b[^>]*?(text-decoration[^"]*underline|text-decoration[^']*underline)[\s\S]*?>(.*?)<\/(u|span)>/i
    ),
    parsedTab: getPatternSnippet(parsedHtml, /\u200B\t+/),
  });
  console.log("htmlPreview", {
    mammothHtml: truncateSnippet(mammothHtml),
    parsedHtml: truncateSnippet(parsedHtml),
  });
  console.groupEnd();
};

export type WordDocumentHtmlConversionResult = {
  html: string;
  headers?: ImportedRevisionHeaders;
  importedChecklist?: ImportedChecklist;
};

export type WordDocumentHtmlImportOptions = {
  extractWritingCompanionChecklist?: boolean;
};

export const useConvertGoogleDocToHtmlWithHeaders = async (
  file: any,
  options: WordDocumentHtmlImportOptions = {}
): Promise<WordDocumentHtmlConversionResult | undefined> => {
  if (file?.data == undefined) {
    throw new Error("File data is undefined");
  }

  const zip = await JSZip.loadAsync(file.data);
  const documentXml = await zip.file("word/document.xml")?.async("string");
  const stylesXml = await zip.file("word/styles.xml")?.async("string");
  const numberingXml = await zip.file("word/numbering.xml")?.async("string");
  const relsXml = await zip
    .file("word/_rels/document.xml.rels")
    ?.async("string");

  if (!documentXml) {
    throw new Error("Failed to parse Word document");
  }

  const arrayBuffer = getSafeArrayBuffer(file.data);

  const result = await mammoth.convertToHtml(
    { arrayBuffer },
    useWordDocumentHtmlConversionOptions() as any
  );
  const mammothHtml = result.value;
  let html = mammothHtml;

  html = useParseWordDocumentStyles(documentXml, html, stylesXml, numberingXml);
  html = useStripWordDocumentImagesFromHtml(html);
  const checklistExtraction = options.extractWritingCompanionChecklist
    ? useExtractWritingCompanionChecklist({ documentXml, html })
    : { html };
  const headers = await useExtractDocxPageHeaders(zip, documentXml, relsXml);
  logGoogleDocConversionDebug({
    documentXml,
    stylesXml,
    mammothHtml,
    parsedHtml: checklistExtraction.html,
  });

  return {
    html: checklistExtraction.html,
    headers,
    importedChecklist: checklistExtraction.importedChecklist,
  };
};

export const useConvertGoogleDocToHtml = async (
  file: any,
  options: WordDocumentHtmlImportOptions = {}
): Promise<string | undefined> => {
  return (await useConvertGoogleDocToHtmlWithHeaders(file, options))?.html;
};
